import { useState, useEffect } from 'react'
import type { Plugin, AvailableSession, SwitcherRoute } from '../api/client'
import { api } from '../api/client'

interface NewChatModalProps {
  workspaceId: string
  plugins: Plugin[]
  onClose: () => void
  onSubmit: (config: {
    name?: string
    agentPlugin?: string
    agentSessionId?: string
    connectionType: 'pty' | 'cli'
    spawnMode?: 'tmux' | 'native-pty'
    model_id?: string
  }) => Promise<void>
  onOpenExistingChat: (chatId: string) => void
}

// Derive agent groupings from the plugin's declared CAPABILITIES, not a hardcoded name list
// (so a new agent drops in with no edits here). A shell (bash/zsh/…) declares cleanView:false
// → "CLI" tab; an AI agent declares cleanView:true → "PTY" tab. Resumable = plugin.canResume.
const isCli = (p: Plugin): boolean => !p.capabilities?.cleanView
const isResumable = (p: Plugin): boolean => !!p.canResume
type Tab = 'new' | 'resume'

export function NewChatModal({ plugins, onClose, onSubmit, onOpenExistingChat }: NewChatModalProps) {
  const [tab, setTab] = useState<Tab>('new')
  const [connType, setConnType] = useState<'pty' | 'cli'>('pty')
  const [selectedPlugin, setSelectedPlugin] = useState('')
  // Phase F: which HarnessCLI runs the chosen Agent. 'native' = the agent's own CLI; otherwise the
  // name of a switchable harness (e.g. 'claude') → run the agent's brain under that harness.
  const [harnessChoice, setHarnessChoice] = useState('native')
  // Phase F งาน3: which routed model to run under that harness. '' = use the agent's default
  // switcherModel. Lets the user pick among several models of the same provider (e.g. grok-4.3
  // vs grok-3-mini). Populated from /api/switcher/routes (allowlist-filtered server-side).
  const [modelChoice, setModelChoice] = useState('')
  const [switcherRoutes, setSwitcherRoutes] = useState<SwitcherRoute[]>([])
  const [chatName, setChatName] = useState('')
  const [submitting, setSubmitting] = useState(false)

  // Default agent from app settings (used to pre-select on first render)
  const [defaultAgent, setDefaultAgent] = useState<string | null>(null)
  const [defaultAgentLoaded, setDefaultAgentLoaded] = useState(false)

  // Resume tab state
  const [resumePlugin, setResumePlugin] = useState<string>('claude')
  const [sessions, setSessions] = useState<AvailableSession[]>([])
  const [sessionsLoading, setSessionsLoading] = useState(false)
  const [sessionsError, setSessionsError] = useState<string | null>(null)
  const [sessionsCwd, setSessionsCwd] = useState<string>('')

  // Load default agent from settings (only on mount)
  useEffect(() => {
    api.settings.app.get()
      .then(s => {
        if (s.defaultAgent) setDefaultAgent(s.defaultAgent)
        setDefaultAgentLoaded(true)
      })
      .catch(() => setDefaultAgentLoaded(true))
  }, [])

  // Pick the right tab + plugin based on default agent (once loaded)
  useEffect(() => {
    if (!defaultAgentLoaded || !defaultAgent) return
    const da = plugins.find(p => p.name === defaultAgent)
    setConnType(da && isCli(da) ? 'cli' : 'pty')
  }, [defaultAgentLoaded, defaultAgent, plugins])

  // Auto-select first plugin when connType changes
  // Prefer the saved defaultAgent if it matches the current connType tab
  useEffect(() => {
    if (!defaultAgentLoaded) return
    const list =
      connType === 'pty'
        ? plugins.filter(p => !isCli(p))
        : plugins.filter(isCli)
    const preferred = defaultAgent && list.find(p => p.name === defaultAgent)?.name
    setSelectedPlugin(preferred ?? list[0]?.name ?? '')
  }, [connType, plugins, defaultAgent, defaultAgentLoaded])

  // Reset HarnessCLI to Native + clear the model override whenever the Agent changes (different
  // agents have different switchable harnesses / default brains; never carry a stale pick across).
  useEffect(() => { setHarnessChoice('native'); setModelChoice('') }, [selectedPlugin])

  // Load the routed models available to the switcher (allowlist-filtered server-side). Best-effort.
  useEffect(() => {
    api.switcher.routes().then(setSwitcherRoutes).catch(() => setSwitcherRoutes([]))
  }, [])

  // Load available sessions when Resume tab opens or plugin changes
  useEffect(() => {
    if (tab !== 'resume') return
    setSessionsLoading(true)
    setSessionsError(null)
    api.chats.availableSessions({ plugin: resumePlugin })
      .then(r => { setSessions(r.sessions); setSessionsCwd(r.cwd) })
      .catch(err => setSessionsError(err.message))
      .finally(() => setSessionsLoading(false))
  }, [tab, resumePlugin])

  // Phase F HarnessCLI options for the chosen Agent: Native + every switchable harness, but only
  // when this agent declares a switcherModel (a brain id that a route can reach). selectedAgentP/
  // switchableHarnesses also drive the dropdown render below.
  const selectedAgentP     = plugins.find(p => p.name === selectedPlugin)
  const switchableHarnesses = plugins.filter(p => p.switchable && p.name !== selectedPlugin)
  const canSwitch          = !!selectedAgentP?.switcherModel && switchableHarnesses.length > 0

  // งาน3 model picker: offer the routed models that share the agent's default-brain provider
  // (e.g. Grok agent → all xai models). Always include the agent's own switcherModel so it is
  // selectable even before a route exists for it. effectiveModel = the user's pick or that default.
  const agentProvider = switcherRoutes.find(r => r.model === selectedAgentP?.switcherModel)?.provider
  const modelOptions: SwitcherRoute[] = (() => {
    const opts = (agentProvider ? switcherRoutes.filter(r => r.provider === agentProvider) : switcherRoutes).slice()
    const sm = selectedAgentP?.switcherModel
    if (sm && !opts.some(o => o.model === sm)) opts.unshift({ model: sm })
    return opts
  })()
  const effectiveModel = modelChoice || selectedAgentP?.switcherModel

  const handleCreateNew = async () => {
    setSubmitting(true)
    try {
      // Native → run the agent's own CLI. Switched → run the agent's brain (switcherModel) UNDER
      // the chosen harness (agentPlugin = harness, model_id = brain). Backend injects the harness's
      // proxy env from the routes config (ptyManager.buildSwitcherEnv).
      const useHarness = canSwitch && harnessChoice !== 'native'
      await onSubmit({
        name:           chatName.trim() || undefined,
        agentPlugin:    (useHarness ? harnessChoice : selectedPlugin) || undefined,
        model_id:       useHarness ? effectiveModel : undefined,
        connectionType: connType,
        spawnMode:      'native-pty', // v2 only — no tmux/spawn choice anymore
      })
      onClose()
    } catch (err) {
      console.error('[NewChatModal] submit error:', err)
    } finally {
      setSubmitting(false)
    }
  }

  const handleResume = async (session: AvailableSession) => {
    // If this session is already linked to an existing Whisper chat,
    // auto-select that chat (don't create a duplicate).
    if (session.linkedChatId) {
      onOpenExistingChat(session.linkedChatId)
      onClose()
      return
    }
    setSubmitting(true)
    try {
      await onSubmit({
        agentPlugin:    resumePlugin,
        agentSessionId: session.sessionId,
        connectionType: 'pty',
        spawnMode:      'native-pty', // v2 only
      })
      onClose()
    } catch (err) {
      console.error('[NewChatModal] resume error:', err)
    } finally {
      setSubmitting(false)
    }
  }

  const ptyPlugins = plugins.filter(p => !isCli(p))
  const cliPlugins = plugins.filter(isCli)
  const visiblePlugins = connType === 'pty' ? ptyPlugins : cliPlugins
  const resumablePluginsAvailable = plugins.filter(isResumable)

  const formatAgo = (ms: number): string => {
    const diff = Date.now() - ms
    const min = Math.floor(diff / 60_000)
    if (min < 1) return 'just now'
    if (min < 60) return `${min}m ago`
    const hr = Math.floor(min / 60)
    if (hr < 24) return `${hr}h ago`
    const day = Math.floor(hr / 24)
    return `${day}d ago`
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal new-chat-modal" onClick={e => e.stopPropagation()}>
        <h3>New Chat</h3>

        {/* Tab selector */}
        <div className="conn-type-selector" style={{ marginBottom: 16 }}>
          <button
            className={`conn-type-btn${tab === 'new' ? ' conn-type-btn--active' : ''}`}
            onClick={() => setTab('new')}
            type="button"
          >
            New Session
          </button>
          <button
            className={`conn-type-btn${tab === 'resume' ? ' conn-type-btn--active' : ''}`}
            onClick={() => setTab('resume')}
            type="button"
          >
            Resume Existing
          </button>
        </div>

        {tab === 'new' ? (
          <>
            {/* Connection type */}
            <div className="modal-label">
              Connection Type
              <div className="conn-type-selector">
                <button
                  className={`conn-type-btn${connType === 'pty' ? ' conn-type-btn--active' : ''}`}
                  onClick={() => setConnType('pty')}
                  type="button"
                >
                  PTY <span className="conn-badge conn-badge--pty">AI</span>
                </button>
                <button
                  className={`conn-type-btn${connType === 'cli' ? ' conn-type-btn--active' : ''}`}
                  onClick={() => setConnType('cli')}
                  type="button"
                >
                  CLI <span className="conn-badge conn-badge--cli">Terminal</span>
                </button>
              </div>
            </div>

            {/* Agent selector */}
            <label className="modal-label">
              Agent
              <select
                className="modal-input"
                value={selectedPlugin}
                onChange={e => setSelectedPlugin(e.target.value)}
              >
                {visiblePlugins.length === 0 ? (
                  <option value="" disabled>
                    No agents available
                  </option>
                ) : (
                  visiblePlugins.map(p => (
                    <option key={p.name} value={p.name}>
                      {p.displayName}{p.name === defaultAgent ? ' (default)' : ''}
                    </option>
                  ))
                )}
              </select>
            </label>

            {/* HarnessCLI selector (Phase F) — appears after Agent. Default "Native" (the agent's
                own CLI). When the agent's brain can run under another harness (switcherModel +
                a switchable harness in the system), offer those too — e.g. Agent: Grok ·
                HarnessCLI: Claude Code → Grok brain rendered with Claude's UI/reader. */}
            <label className="modal-label">
              HarnessCLI
              <select
                className="modal-input"
                value={harnessChoice}
                onChange={e => setHarnessChoice(e.target.value)}
              >
                <option value="native">Native ({selectedAgentP?.displayName ?? 'agent'}'s own CLI)</option>
                {canSwitch && switchableHarnesses.map(h => (
                  <option key={h.name} value={h.name}>{h.displayName}</option>
                ))}
              </select>
              {harnessChoice !== 'native' && (
                <span className="modal-hint" style={{ display: 'block', marginTop: 4 }}>
                  รัน <b>{selectedAgentP?.displayName}</b> ({effectiveModel}) ด้วย harness ของ <b>{switchableHarnesses.find(h => h.name === harnessChoice)?.displayName}</b>
                </span>
              )}
            </label>

            {/* Model picker (Phase F งาน3) — only when running under a non-Native harness. Lists the
                routed models for this agent's brain provider; default = the agent's switcherModel. */}
            {harnessChoice !== 'native' && modelOptions.length > 1 && (
              <label className="modal-label">
                Model
                <select
                  className="modal-input"
                  value={effectiveModel ?? ''}
                  onChange={e => setModelChoice(e.target.value)}
                >
                  {modelOptions.map(m => (
                    <option key={m.model} value={m.model}>
                      {m.model}{m.provider ? ` · ${m.provider}` : ''}
                    </option>
                  ))}
                </select>
              </label>
            )}

            {/* Name */}
            <label className="modal-label">
              Name <span style={{ color: 'var(--text-muted)' }}>(optional)</span>
              <input
                className="modal-input"
                value={chatName}
                onChange={e => setChatName(e.target.value)}
                placeholder="Auto-generated if empty"
                onKeyDown={e => {
                  if (e.key === 'Enter') handleCreateNew()
                }}
              />
            </label>

            <div className="modal-actions">
              <button onClick={onClose} className="btn-ghost" type="button">
                Cancel
              </button>
              <button
                onClick={handleCreateNew}
                className="btn-primary"
                disabled={submitting}
                type="button"
              >
                {submitting ? '...' : 'Create Chat'}
              </button>
            </div>
          </>
        ) : (
          <>
            {/* Resume tab: pick which agent's sessions to browse */}
            {resumablePluginsAvailable.length > 1 && (
              <label className="modal-label">
                Agent
                <select
                  className="modal-input"
                  value={resumePlugin}
                  onChange={e => setResumePlugin(e.target.value)}
                >
                  {resumablePluginsAvailable.map(p => (
                    <option key={p.name} value={p.name}>{p.displayName}</option>
                  ))}
                </select>
              </label>
            )}

            <p className="modal-hint" style={{ marginBottom: 12 }}>
              Sessions from <code>{sessionsCwd || '$HOME'}</code> — same list as <code>{resumePlugin} --resume</code> in your terminal.
            </p>

            {sessionsLoading && <div className="chat-turns-loading">Loading sessions...</div>}
            {sessionsError && <div style={{ color: 'var(--danger)' }}>Error: {sessionsError}</div>}

            <div className="session-list">
              {!sessionsLoading && sessions.length === 0 && (
                <div className="chat-turns-empty">No sessions found for {resumePlugin}</div>
              )}
              {sessions.map(s => (
                <button
                  key={s.sessionId}
                  className={`session-item${s.linkedChatId ? ' session-item--linked' : ''}`}
                  onClick={() => handleResume(s)}
                  disabled={submitting}
                  type="button"
                >
                  <div className="session-item-title">
                    {s.title ?? <span style={{ color: 'var(--text-muted)' }}>Untitled</span>}
                    {s.linkedChatId && (
                      <span className="session-item-badge" title="Click to open existing chat">
                        ↗ Open existing
                      </span>
                    )}
                  </div>
                  {s.firstUserMessage && (
                    <div className="session-item-preview">{s.firstUserMessage}</div>
                  )}
                  <div className="session-item-meta">
                    {s.turnCount} turns · {formatAgo(s.lastActivity)} · <code>{s.sessionId.slice(0, 8)}</code>
                  </div>
                </button>
              ))}
            </div>

            <div className="modal-actions">
              <button onClick={onClose} className="btn-ghost" type="button">
                Close
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
